import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientLineDescription } from "../../../../../client/components/ClientLineDescription";
import { ClientFormType } from "../../../../../client/enums";
import { InsuranceContract } from "../../../../types";
import { getClientsDataFromInsuranceContract } from "../../../../utils";
import { ContractAgentsData } from "../../../ContractAgentsData";
import { ContractClientsData } from "../../../ContractClientsData";
import { createCancellationDateDescriptionItem } from "../../../description-items/cancellationDateDescriptionItem";
import { createEffectiveEndDateDescriptionItem } from "../../../description-items/effectiveEndDateDescriptionItem";
import { createNoteDescriptionItem } from "../../../description-items/noteDescriptionItem";
import { ContractInsurancesData } from "./ContractInsurancesData";

interface Props {
  contract: InsuranceContract;
}

export const InsuranceContractDataSection = ({ contract }: Props) => {
  const { t } = useTranslation();

  const clientsData = getClientsDataFromInsuranceContract(contract);

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: 180, fontWeight: "bold" } }}
      items={[
        {
          label: t("contract.sections.clients"),
          children: clientsData && (
            <ContractClientsData
              items={clientsData.map((clientData) => {
                const clientLabel = clientData.clientFormTypes?.length
                  ? clientData.clientFormTypes
                      .map((type) =>
                        type === ClientFormType.POLICY_HOLDER && clientData.clientPolicyHolderType
                          ? t("client.helpers." + clientData.clientPolicyHolderType + "PolicyHolder")
                          : t("client.enums.formType." + type),
                      )
                      .join(", ")
                  : t("client.helpers.client");

                return {
                  label: clientLabel,
                  children: <ClientLineDescription client={clientData.client} />,
                };
              })}
            />
          ),
        },
        {
          label: t("contract.sections.contractData"),
          children: (
            <Descriptions
              bordered
              size="small"
              column={3}
              styles={{ content: { whiteSpace: "nowrap" } }}
              items={[
                {
                  label: t("contract.attrs.mediationReportSignDate"),
                  children: contract.mediationReportSignDate
                    ? formatLocaleDate(contract.mediationReportSignDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.signDateInsurance"),
                  children: formatLocaleDate(contract.signDate),
                },
                {
                  label: t("contract.attrs.effectiveBeginningDate"),
                  children: formatLocaleDate(contract.effectiveBeginningDate),
                },

                {
                  label: t("contract.enums.periodType._label"),
                  children: t(`contract.enums.periodType.${contract.periodType}`),
                },
                createEffectiveEndDateDescriptionItem(contract),
                createCancellationDateDescriptionItem(contract),
                {
                  label: t("contract.enums.paymentFrequency._label"),
                  children: t(`contract.enums.paymentFrequency.${contract.paymentFrequency}`),
                },
                {
                  label: t("contract.attrs.annualPremium"),
                  children: formatLocaleCurrency(contract.annualPremium),
                },
                {
                  label: t("contract.attrs.partialPremium"),
                  children: formatLocaleCurrency(contract.partialPremium),
                },
                {
                  label: t("contract.enums.insurancePeriod._label"),
                  children: t(`contract.enums.insurancePeriod.${contract.insurancePeriod}`),
                },
                {
                  label: t("contract.attrs.insurancePeriodEndDate"),
                  children: formatLocaleDate(contract.insurancePeriodEndDate),
                },
                {
                  label: t("contract.attrs.lastContractCancellationDate"),
                  children: contract.lastContractCancellationDate
                    ? formatLocaleDate(contract.lastContractCancellationDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.transferredFromOtherBroker"),
                  children: tBoolean(contract.transferredFromOtherBroker),
                },
                {
                  label: t("contract.attrs.transferredToOtherBroker"),
                  children: contract.transferredToOtherBrokerDate
                    ? formatLocaleDate(contract.transferredToOtherBrokerDate)
                    : undefined,
                  span: 2,
                },
                {
                  label: t("contract.attrs.paidUntilDate"),
                  children: contract.paidUntilDate ? formatLocaleDate(contract.paidUntilDate) : undefined,
                },
                {
                  label: t("contract.attrs.amountOwed"),
                  children: isDefined(contract.amountOwed) ? formatLocaleCurrency(contract.amountOwed) : undefined,
                  span: 2,
                },
                createNoteDescriptionItem(contract),
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.insurances"),
          children: <ContractInsurancesData contract={contract} />,
        },
        {
          label: t("contract.sections.agents"),
          children: <ContractAgentsData contract={contract} />,
        },
      ]}
    />
  );
};
